// src/services/adminApi.ts
import { 
    PromoCode, 
    PaginatedResponse, 
    RedemptionHistoryItem, 
    RedemptionStats, 
    PromoCodeAnalytics,
    PromoCodeCreateRequest,
    PromoCodeUpdateRequest,
    BatchGenerationRequest,
    BulkOperationResult,
    AuditLogEntry,
    UserStatistics
  } from '../types/admin';
  
  const API_URL = '/api';
  
  /**
   * API service for admin operations
   */
  export const adminApi = {
    /**
     * Get promo codes with pagination and filtering
     */
    getPromoCodes: async (params?: Record<string, any>): Promise<PaginatedResponse<PromoCode>> => {
      try {
        // Build query string from params
        const queryParams = params ? new URLSearchParams(cleanParams(params)).toString() : '';
        
        const response = await fetch(`${API_URL}/promo-codes?${queryParams}`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(await handleErrorResponse(response));
        }
        
        return await response.json();
      } catch (error) {
        console.error('Error fetching promo codes:', error);
        throw error;
      }
    },
    
    /**
     * Get a single promo code by ID
     */
    getPromoCode: async (id: string): Promise<PromoCode> => {
      try {
        const response = await fetch(`${API_URL}/promo-codes/${id}`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(await handleErrorResponse(response));
        }
        
        return await response.json();
      } catch (error) {
        console.error(`Error fetching promo code ${id}:`, error);
        throw error;
      }
    },
    
    /**
     * Create a new promo code
     */
    createPromoCode: async (data: PromoCodeCreateRequest): Promise<PromoCode> => {
      try {
        const response = await fetch(`${API_URL}/promo-codes/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data),
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(await handleErrorResponse(response));
        }
        
        return await response.json();
      } catch (error) {
        console.error('Error creating promo code:', error);
        throw error;
      }
    },
    
    /**
     * Update an existing promo code
     */
    updatePromoCode: async (id: string, data: PromoCodeUpdateRequest): Promise<PromoCode> => {
      try {
        const response = await fetch(`${API_URL}/promo-codes/${id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data),
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(await handleErrorResponse(response));
        }
        
        return await response.json();
      } catch (error) {
        console.error(`Error updating promo code ${id}:`, error);
        throw error;
      }
    },
    
    /**
     * Delete a promo code
     */
    deletePromoCode: async (id: string): Promise<void> => {
      try {
        const response = await fetch(`${API_URL}/promo-codes/${id}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(await handleErrorResponse(response));
        }
      } catch (error) {
        console.error(`Error deleting promo code ${id}:`, error);
        throw error;
      }
    },
    
    /**
     * Generate a batch of promo codes
     */
    generateBatchPromoCodes: async (data: BatchGenerationRequest): Promise<PromoCode[]> => {
      try {
        const response = await fetch(`${API_URL}/promo-codes/generate-batch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data),
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(await handleErrorResponse(response));
        }
        
        return await response.json();
      } catch (error) {
        console.error('Error generating batch promo codes:', error);
        throw error;
      }
    },
    
    /**
     * Get redemption history for a promo code
     */
    getRedemptionHistory: async (code: string): Promise<RedemptionHistoryItem[]> => {
      try {
        const response = await fetch(`${API_URL}/promo-codes/redemptions/${code}`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(await handleErrorResponse(response));
        }
        
        return await response.json();
      } catch (error) {
        console.error(`Error fetching redemption history for code ${code}:`, error);
        throw error;
      }
    },
    
    /**
     * Get redemption statistics for a promo code
     */
    getRedemptionStats: async (code: string): Promise<RedemptionStats> => {
      try {
        const response = await fetch(`${API_URL}/promo-codes/stats/${code}`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(await handleErrorResponse(response));
        }
        
        return await response.json();
      } catch (error) {
        console.error(`Error fetching redemption stats for code ${code}:`, error);
        throw error;
      }
    },
    
    /**
     * Get promo code analytics
     */
    getPromoCodeAnalytics: async (params?: Record<string, string>): Promise<PromoCodeAnalytics> => {
      try {
        // Build query string from params
        const queryParams = params ? new URLSearchParams(cleanParams(params)).toString() : '';
        
        const response = await fetch(`${API_URL}/promo-codes/analytics/overview?${queryParams}`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(await handleErrorResponse(response));
        }
        
        return await response.json();
      } catch (error) {
        console.error('Error fetching promo code analytics:', error);
        throw error;
      }
    },
    
    /**
     * Bulk deactivate promo codes
     */
    bulkDeactivatePromoCodes: async (ids: string[]): Promise<BulkOperationResult> => {
      try {
        const response = await fetch(`${API_URL}/promo-codes/bulk-deactivate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ ids }),
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(await handleErrorResponse(response));
        }
        
        return await response.json();
      } catch (error) {
        console.error('Error bulk deactivating promo codes:', error);
        throw error;
      }
    },
    
    /**
     * Get platform user statistics (admin only)
     */
    getStatistics: async (): Promise<UserStatistics> => {
      try {
        const response = await fetch(`${API_URL}/admin/statistics`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(await handleErrorResponse(response));
        }
        
        return await response.json();
      } catch (error) {
        console.error('Error fetching user statistics:', error);
        throw error;
      }
    },
    
    /**
     * Get audit logs for admin actions
     */
    getAuditLogs: async (params?: Record<string, any>): Promise<PaginatedResponse<AuditLogEntry>> => {
      try {
        // Build query string from params
        const queryParams = params ? new URLSearchParams(cleanParams(params)).toString() : '';
        
        const response = await fetch(`${API_URL}/admin/audit-logs?${queryParams}`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(await handleErrorResponse(response));
        }
        
        return await response.json();
      } catch (error) {
        console.error('Error fetching audit logs:', error);
        throw error;
      }
    }
  };
  
  /**
   * Clean parameters for query string by removing null and undefined values
   */
  function cleanParams(params: Record<string, any>): Record<string, string> {
    return Object.entries(params)
      .filter(([_, value]) => value !== null && value !== undefined)
      .reduce((acc, [key, value]) => ({
        ...acc,
        [key]: String(value)
      }), {});
  }
  
  /**
   * Handle error responses from the API
   */
  async function handleErrorResponse(response: Response): Promise<string> {
    try {
      const contentType = response.headers.get('content-type');
      
      // Check if response is JSON
      if (contentType && contentType.indexOf('application/json') !== -1) {
        const errorData = await response.json();
        return errorData.error || errorData.message || `Error: HTTP ${response.status}`;
      }
      
      // Fallback to text
      return await response.text() || `Error: HTTP ${response.status}`;
    } catch (error) {
      return `Error: HTTP ${response.status}`;
    }
  }